import ApiDataModel, { GeometryData } from "@/models/apidatamodel"
import DynamicRightSlotData from "@/models/dynamicrighslotdata"
import MarkerUtils from "@/helpers/markerUtils"
import moment from "moment"
import utils from "@/utils/utils"
import ApiService from "@/services/ApiService"
import { GeometryTypes } from "@/helpers/enums"
import { store } from '@/store/store'
import i18n from '@/i18n'

const t = i18n.global.t

export interface Legend {
    languageTerm: string;
    color: string;
}

export default class InfokartatService {
    markerUtils = new MarkerUtils(store.getters.contentServiceUrl, store.getters.contentServiceTimeStamp)
    legendList = new Array<Legend>()
    baseHref = "/" + utils.getCurrentSubdomain()
    apiService = new ApiService(this.baseHref)
    infokarttaApiCallBasePart = "v1/Infokartta/"
    defaultColor = "#009FE366"

    async handleDataInfokartta(data): Promise<ApiDataModel[]> {
        try {
            this.legendList = []
            if (data !== null && data !== undefined && data.length > 0 && data[0].tikuaineistoid !== null && data[0].tikuaineistoid !== undefined) {
                await this.apiService.dynamicApiCall(this.infokarttaApiCallBasePart + "GetLegendByAineistoID?aineistoID=" + data[0].tikuaineistoid).then(async (response) => {
                    // täytetään legend lista vain jos se on tyhjä
                    if (this.legendList.length === 0) {
                        response.forEach((legend) => {
                            let color = legend.piirtovari ?? this.defaultColor
                            if (color.includes("#") === false) {
                                color = "#" + color
                            }
                            this.legendList.push({
                                color,
                                languageTerm: this.currentLanguage === "fi" ? legend.kuvausFi : this.currentLanguage === "sv" ? legend.kuvausSv : legend.kuvausEn
                            })
                        })
                    }
                })
            }

            const apiData = new Array<ApiDataModel>()
            data.forEach((value) => {
                if (value.alue != null) {
                    let color = value.piirtovari ?? this.defaultColor
                    if (color.includes("#") === false) {
                        color = "#" + color
                    }
                    const options = {
                        fillColor: color,
                        color: "#6D6D6D",
                        weight: 1,
                        opacity: 0.5,
                        fillOpacity: 0.8,
                        id: value.id
                    }

                    let geometryType = value.alue.type
                    if (geometryType === GeometryTypes.GeometryCollection) {
                        if (value.alue.geometries !== null && value.alue.geometries !== undefined && value.alue.geometries.length > 0) {
                            geometryType = value.alue.geometries[0].type
                        }
                    }
                    if (geometryType === GeometryTypes.LineString || geometryType === GeometryTypes.MultiLineString) {
                        options.weight = 4
                        options.color = color
                    }

                    value.alue.options = options
                    value.alue.handleOnClick = true
                    const rightSlotData = new DynamicRightSlotData(value.id.toString())
                    // rightSlotData.dataUpdatedDate = this.getUpdatedtext(value.timestamp ?? moment().toISOString()) ?? "?"
                    rightSlotData.textContent = this.currentLanguage === "fi" ? value.kuvausfi : this.currentLanguage === "sv" ? value.kuvaussv : value.kuvausen
                    const extrenlinkIcon = "<img class='external-icon' src='" + this.getExternalLink() + "' alt='" + this.getExternalLinkAlt() + "' />"
                    const linkki = this.currentLanguage === "fi" ? value.linkkifi : this.currentLanguage === "sv" ? value.linkkisv : value.linkkien
                    rightSlotData.linkki = linkki
                        ? "<a href='"
                            + linkki
                            + "' class='open-modal-link' role='button' target='_blank'>"
                            + t("App.RightSlot.Link.Infokartat") + ": "
                            + (this.currentLanguage === "fi" ? value.nimifi : this.currentLanguage === "sv" ? value.nimisv : value.nimien)
                            + extrenlinkIcon
                            + utils.getLinkOpensNewTabSpan() + "</a>"
                        : ""
                    rightSlotData.description = this.currentLanguage === "fi" ? value.infomapkuvausfi : this.currentLanguage === "sv" ? value.infomapkuvaussv : value.infomapkuvausen

                    apiData.push(new ApiDataModel(value.id.toString(), true, value.alue, value, rightSlotData, null))
                }
            })

            return apiData
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    getExternalLink(): string {
        return store.getters.contentServiceUrl + "/Content/icons/svg-blue/linkki-external.svg" + "?" + store.getters.contentServiceTimeStamp
    }

    getExternalLinkAlt(): string | undefined {
        return t("App.Link.UlosJohtavaLinkki")
    }

    getUpdatedtext(time: string, includeText: boolean = true):string {
        const updatedtext = t("App.LaajakaistaJaPuhelin.DataUpdated")
        let updatedTime = "?"
        if (moment(time).format("MM/YYYY")) {
            updatedTime = " " + moment(time).format("MM/YYYY")
        }
        return includeText === true ? updatedtext + updatedTime : updatedTime
    }

    get currentLanguage() {
        return store.getters.activeLang
    }

    get modalHeader(): string {
        return store.getters.header
    }

    openModal() {
        store.dispatch("setModalComponentName", "InfokarttaModal")
    }

    getAlueTyyppiLegend(): string {
        let legend = ""
        this.legendList.forEach((alueLegend) => {
            legend += "<div class=\"legend-item\"><i style=\"background: " + alueLegend.color + "\"></i><div>" + alueLegend.languageTerm + "</div></div>"
        })
        return legend
    }

    getPisteTyyppiLegend(): string {
        let legend = ""
        this.legendList.forEach((pisteLegend) => {
            legend += "<div class=\"legend-item\"><img alt=\"\" src= " + this.markerUtils.createLocationMarkerByColor(pisteLegend.color) + "\">" + pisteLegend.languageTerm + "</div>"
        })
        return legend
    }

    getReittiTyyppiLegend(): string {
        let legend = ""
        this.legendList.forEach((reittiLegend) => {
            legend += "<div class=\"legend-item\"><b style=\"background: " + reittiLegend.color + ";height:4px;margin-top:0px;\"></b><div>" + reittiLegend.languageTerm + "</div></div>"
        })
        return legend
    }
}

export interface Infokartta {
    id: number
    timestamp: string
    kuvausfi: string
    kuvaussv: string
    kuvausen: string
    linkkifi: string
    linkkisv: string
    linkkien: string
    piirtovari: string
    alue: GeometryData | null
}
